<template>
    <div
        class="search"
        :class="{
            [`theme-${layout.theme}`]: true,
        }"
    >
        <button class="ds-btn ds-btn--quartiary search-button" @click="activateSearch">
            <span>{{ $t('search.search') }}</span>
            <DSFontIcon
                code="search"
                size="m"
                icon-style="filled"
                icon-set="st1website"
            />
        </button>
        <Teleport v-if="modal.name === 'search'" :to="modal.modalTarget">
            <div class="ds-modal-content ds-modal-search lenis-ignore">
                <div class="head">
                    <nuxt-link class="logo" :to="$paths.getPath('landingPage')">
                        <object :data="logo.src" :alt="logo.alt" />
                    </nuxt-link>
                    <button class="close-btn" @click="modal.unsetNameAndClose">
                        <DSFontIcon
                            code="close"
                            icon-set="st1website"
                            icon-style="line"
                            size="xl"
                        />
                    </button>
                </div>
                <div class="body">
                    <DSInput
                        v-model="searchPrompt"
                        :icon-after="searchPrompt ? 'delete-text' : 'search'"
                        :label="$t('search.label')"
                        @input="search"
                        @click-after="clearSearchPrompt"
                        @blur="gtmPush('search')"
                    />
                    <div v-if="searchPrompt.length > 0" class="search-results-header">
                        <div v-if="searchResults.total > 0" class="results-summary">
                            <div :aria-label="`${searchResults.total} ${$t('search.searchResultsAmount')}`"><span class="result-data">{{ searchResults.total }}</span> <span class="result-label">{{ $t('search.searchResultsAmount') }}</span></div>
                            <div><span class="result-label">{{ $t('search.searchResultsPages') }}</span> <span class="result-data">{{ `${searchResults.page}/${pages}` }}</span></div>
                        </div>
                        <div v-if="searchResults.total > 0" class="results-options">
                            <button
                                class="ds-btn ds-btn--quartinary"
                                :class="{selected: !searchFilter}"
                                @click="setFilter()"
                            >
                                {{ $t('search.showAll') }}
                            </button>
                            <button
                                v-for="(filter, index) in filters"
                                :key="`filter-${filter}-${index}`"
                                class="ds-btn ds-btn--quartinary"
                                :class="{selected: searchFilter === filter}"
                                @click="() => setFilter(filter)"
                            >
                                {{ $t(`search.${filter}`) }}
                            </button>

                            <DSDropdown ref="sortDropdown">
                                <template #button>
                                    <button
                                        class="ds-btn ds-btn--tertiary sort-button"
                                        :class="{'is-open': sortButtonIsOpen}"
                                        @click="sortButtonIsOpen = !sortButtonIsOpen"
                                    >
                                        <span>{{ $t(`sort.${computedSortTitle}`) }}</span>
                                        <DSFontIcon
                                            code="chevron-right"
                                            icon-style="line"
                                            class="caret-down"
                                            size="l"
                                        />
                                    </button>
                                </template>
                                <template #default="{ close }">
                                    <button @click="setSorting('title', 'asc'); close()">{{ $t('sort.popularity') }}</button>
                                    <button @click="setSorting('createdAt', 'asc'); close()">{{ $t('sort.newestFirst') }}</button>
                                    <button @click="setSorting('createdAt', 'desc'); close()">{{ $t('sort.oldestFirst') }}</button>
                                </template>
                            </DSDropdown>
                        </div>
                        <span v-else-if="!searchLoading">{{ $t('search.noResultsFound') }}</span>
                    </div>
                    <div v-if="!searchResults.total && !searchLoading" class="search-quicklinks">
                        <h3 class="h3">{{ $t('search.quicklinksHeading') }}</h3>
                        <ul v-if="layoutData.searchQuicklinks">
                            <li v-for="(quicklink, index) in layoutData.searchQuicklinks" :key="index">
                                <nuxt-link :to="`/${quicklink.url?.url}`">{{ quicklink.title }}</nuxt-link>
                            </li>
                        </ul>
                    </div>
                    <div v-if="searchLoading" class="loading-indicator"><DSPreloader aria-label="loading search results" round /></div>
                    <LayoutSearchResult
                        v-else
                        :results="searchResults.results"
                        :prompt="searchPrompt"
                        aria-live="polite"
                    />
                    <div v-if="searchPrompt && searchResults.total > 0" class="search-result-nav-buttons">
                        <button
                            class="ds-btn ds-btn--quartinary"
                            :disabled="paginatedPagesPage < 1"
                            @click="paginatedPagesPage = paginatedPagesPage-1"
                        ><DSFontIcon
                            code="arrow-left"
                            icon-set="st1website"
                            icon-style="filled"
                        /></button>
                        <button
                            v-if="paginatedPagesPage > 0"
                            class="ds-btn ds-btn--quartinary"
                            @click="loadFirstPage()"
                        >1</button>
                        <button
                            v-if="paginatedPagesPage > 0"
                            class="ds-btn ds-btn--quartinary"
                        >...</button>
                        <button
                            v-for="(page, index) in paginatedPages[paginatedPagesPage]"
                            :key="index"
                            class="ds-btn ds-btn--quartinary"
                            :class="{selected: page === searchResults.page }"
                            @click="loadPage(page)"
                        >{{ page }}</button>
                        <button
                            v-if="paginatedPages[paginatedPagesPage].length === paginatedPagesPerPage"
                            class="ds-btn ds-btn--quartinary"
                        >...</button>
                        <button
                            v-if="paginatedPages[paginatedPagesPage].length === paginatedPagesPerPage"
                            class="ds-btn ds-btn--quartinary"
                            @click="loadLastPage()"
                        >{{ pages }}</button>
                        <button
                            class="ds-btn ds-btn--quartinary"
                            :disabled="paginatedPagesPage === paginatedPages.length - 1"
                            @click="paginatedPagesPage = paginatedPagesPage + 1"
                        ><DSFontIcon code="arrow-right" icon-style="filled" icon-set="st1website" /></button>
                    </div>
                </div>
            </div>
        </Teleport>
        <LayoutKeyboardEvents @keydown="keyboardEvent" />
    </div>
</template>
<script setup>
import {
    ref,
    inject,
    useNuxtApp,
    useCmsProxy,
    watch,
    useRoute,
    useI18n,
    computed,
    storeToRefs,
    useAnalytics,
} from '#imports';
import { useModalStore } from '~dsc/stores/modal';
import { useLayoutStore } from '~dsc/stores/cms/layout';

const { gtm } = useAnalytics();
const modal = useModalStore();
const layout = inject('layout');
const sortButtonIsOpen = ref(false);
const route = useRoute();
const { $paths } = useNuxtApp();
const paginatedPagesPage = ref(0);
const paginatedPagesPerPage = ref(10);

const { layoutData } = await storeToRefs(useLayoutStore());

const debounceTimeout = ref(null);

const searchPrompt = ref('');
const searchLoading = ref(false);
const searchResults = ref([]);

const searchFilter = ref();
const sortBy = ref('title');
const sortDirection = ref('asc');

const searchResultsLimit = 5;
const searchResultsPage = ref(1);

const pages = computed(() => Math.ceil(searchResults.value.total/searchResultsLimit));

const paginatedPages = computed(() => {
    if (!searchResults.value.total) return null;

    const pages = Math.ceil(searchResults.value.total/searchResultsLimit);
    const numbers = [];
    const chunked = [];

    for (let i = 1; i <= pages; i++) {
        numbers.push(i);
    }

    for (let i = 0; i < numbers.length; i += paginatedPagesPerPage.value) {
        chunked.push(numbers.slice(i, i + paginatedPagesPerPage.value));
    }

    return chunked;

});

const logo = computed(()=>{
    if (!layoutData.logo) return { src: '/images/logo.svg', alt: 'logoAlt' };
    const { 0: logo } = layoutData.logo;

    return logo;
});

const filters = computed(() => [...new Set(searchResults.value.results.map(result => result.content.contentType))]);
const computedSortTitle = computed(() => {
    if (sortBy.value === 'createdAt') return sortDirection.value === 'asc' ? 'newestFirst' : 'oldestFirst';

    return 'popularity';
});

watch(() => route.path, () => {
    searchResults.value = [];
    searchPrompt.value = '';
    modal.unsetNameAndClose();
});

const clearSearchPrompt = () => {
    searchResults.value = [];
    searchPrompt.value = '';
};

const search = () => {
    if (debounceTimeout.value) {
        clearTimeout(debounceTimeout.value);
    }

    debounceTimeout.value = setTimeout(async () => {
        const MIN_PROMPT_LENGTH = 3;

        if (searchPrompt.value.length < MIN_PROMPT_LENGTH) {
            searchResults.value = [];
            searchResultsPage.value = 1;

            return;
        }
        searchLoading.value = true;

        const body = {
            'textQuery': searchPrompt.value,
            'page': searchResultsPage.value,
            'limit': searchResultsLimit,
            'sort': {
                'sortBy': sortBy.value,
                'sortDirection': sortDirection.value,
            },
        };

        try {
            const res = await useCmsProxy($paths.getEndpoint('search'), { method: 'POST', body });
            searchResults.value = res;
            searchLoading.value = false;

        } catch (error) {
            console.error(error);
            searchLoading.value = false;

        } finally {
            searchLoading.value = false;
        }
        searchLoading.value = false;
    }, 500);
};

const activateSearch = () => {
    modal.setNameAndOpen('search');
    layout.value.sidebars.right = false;
};

const setFilter = filter => {
    searchFilter.value = filter;
    search();
    gtmPush();
};

const setSorting = (getSortBy, getSortDirection) => {
    sortBy.value = getSortBy;
    sortDirection.value = getSortDirection;
    search();
};

const loadPage = page => {
    searchResultsPage.value = page;
    search(searchPrompt.value);
};

const loadFirstPage = () => {
    searchResultsPage.value = 1;
    paginatedPagesPage.value = 0;
    search(searchPrompt.value);
};

const loadLastPage = () => {
    searchResultsPage.value = pages.value;
    paginatedPagesPage.value = paginatedPages.value.length - 1;
    search(searchPrompt.value);
};

const gtmPush = type => {
    if (searchPrompt.value.length < 3 ) return;

    let event = {
        event: 'search',
        search_term: searchPrompt.value,
        search_category: searchFilter.value,
    };

    if (searchResults.value.total < 1) {
        event = Object.assign(event, {
            event: 'struggle',
            gtm: {
                error_text: 'search with zero results',
                fatal: 'false',
                error_status: 404,
                error_code: 'failed.search',
                error_type: 'struggle',
            },
        });
    }

    if (type === 'search') {
        gtm().trackEvent(event);
    }
};

const keyboardEvent = e => {
    switch (e.which) {
        case 191:
            modal.setNameAndOpen('search');
            break;

        default:
            break;
    }

};

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            search: {
                search: 'Haku',
                label: 'Hae sivustolta',
                searchResultsExplanation: 'Hakusanalla { searchPrompt } löytyi { total } tulosta. Näytetään { itemsPerPage } tulosta per sivu. Sivu { page }/{ pages }',
                searchResultsAmount: 'Hakutulokset',
                searchResultsPages: 'Sivu',
                popularity: 'Suosituin ensin',
                noResultsFound: '- Ei hakutuloksia',
                quicklinksHeading: 'Pikalinkit',
                showAll: 'Näytä kaikki',
                products: 'Tuotteet',
                standardPage: 'Sivu',
                stationPageTemplate: 'Asemat',
                articlePage: 'Artikkelit',
            },
            sort: {
                popularity: 'Suosituin ensin',
                newestFirst: 'Uusin ensin',
                oldestFirst: 'Vanhin ensin',
            },
        },
        'en-EU': {
            search: {
                search: 'Search',
                label: 'Search',
                searchResultsExplanation: 'Hakusanalla { searchPrompt } löytyi { total } tulosta. Näytetään { itemsPerPage } tulosta per sivu. Sivu { page }/{ pages }',
                searchResultsAmount: 'Search results',
                searchResultsPages: 'Page',
                popularity: 'Popularity',
                noResultsFound: '- No search results, please try a different search term or use the quicklinks below',
                quicklinksHeading: 'Quicklinks',
                showAll: 'Show all',
                products: 'Products',
                standardPage: 'Pages',
                stationPageTemplate: 'Stations',
                articlePage: 'Articles',
            },
            sort: {
                popularity: 'Popularity',
                newestFirst: 'Newest First',
                oldestFirst: 'Oldest First',
            },
        },
        'sv-SE': {
            search: {
                search: 'Sök',
                label: 'Sök',
                searchResultsExplanation: 'Sökresultat',
                searchResultsAmount: 'Sökresultat',
                searchResultsPages: 'sida',
                popularity: 'Populäritet',
                noResultsFound: '- Inga resultat hittades, vänligen försök ändra din sökning eller använd snabblänkarna nedan',
                quicklinksHeading: 'Snabblänkar',
                showAll: 'Visa all',
                products: 'Produkter',
                standardPage: 'Sidor',
                stationPageTemplate: 'Stationer',
                articlePage: 'Artiklar',
            },
            sort: {
                popularity: 'Populäritet',
                newestFirst: 'Nyast först',
                oldestFirst: 'Äldst först',
            },
        },
        'no-NO': {
            search: {
                search: 'Søk',
                label: 'Søk',
                searchResultsExplanation: 'Søkeresultat',
                searchResultsAmount: 'Søkeresultat',
                searchResultsPages: 'side',
                popularity: 'Populæritet',
                noResultsFound: '- Ingen resultat funnet',
                quicklinksHeading: 'Hurtigkoblinger',
                showAll: 'Vis alle',
                products: 'Produkter',
                standardPage: 'Sider',
                stationPageTemplate: 'Stasjoner',
                articlePage: 'Artikler',
            },
            sort: {
                popularity: 'Populæritet',
                newestFirst: 'Nyest først',
                oldestFirst: 'Eldst først',
            },
        },
    },
});

</script>
<style scoped>
.search {
  position: relative;
}
.search.theme-clear {
  color: var(--color-base-white);
}
.search.theme-clear .ds-btn {
  background: transparent;
  border-color: transparent;
  color: inherit;
}
@media (width >= 1439px) {
  .search.theme-clear .ds-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: transparent;
  }
}
.search.theme-white {
  background: transparent;
}
.search.theme-white .ds-btn {
  border-color: transparent;
}
.search.theme-white .ds-btn:hover {
  background: var(--color-base-grey-100);
  border-color: var(--color-base-grey-100);
}
@media (width <= 1439px) {
  .search.theme-white, .search.theme-clear {
    color: var(--color-base-black);
  }
  .search.theme-white .ds-btn, .search.theme-clear .ds-btn {
    padding: 1rem;
    border: 0.125rem solid var(--color-secondary-150);
    border-radius: 0.5rem;
    width: 100%;
    justify-content: space-between;
    font-weight: 400;
  }
}
.search button.ds-btn {
  border-width: 2px;
  border-style: solid;
  align-items: center;
}
.search button.ds-btn span {
  display: grid;
  align-items: center;
}
.search.theme-black .label {
  color: var(--color-base-white);
}
.search.theme-black .option {
  color: var(--color-base-white);
}
.search.theme-black .option.selected {
  color: var(--color-secondary-500);
  border-bottom: 0.25rem solid var(--color-secondary-500);
}
.search .search-button {
  display: inline-flex;
  align-items: center;
}
.search .search-button .icon {
  margin-left: 0.5rem;
}
.search .label {
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  padding: 0;
  min-height: 1.5rem;
  min-width: 35px;
  justify-content: flex-end;
  font-weight: 400;
}
.search .label .icon {
  transition: all 0.2s ease-in-out;
  position: relative;
  max-width: 0.5rem;
  margin-left: 0.25rem;
  transform: rotate(0) translateY(0);
  transform-origin: center center;
}
.search .label:focus-visible {
  outline: -webkit-focus-ring-color auto 1px;
}

.ds-modal-search .ds-input .icon {
  margin-top: 0.5rem;
}
.ds-modal-search .body {
  overflow-x: hidden;
  padding-bottom: 0;
}
.ds-modal-search input:focus + .label {
  position: relative;
  top: -1rem;
  font-size: 0.5rem;
  line-height: 0.5rem;
}
.ds-modal-search input.has-text + .label {
  position: relative;
  top: -1rem;
  font-size: 0.5rem;
  line-height: 0.5rem;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  gap: 4rem;
  margin-top: 1rem;
}
@media (width <= 1439px) {
  .search-results-header {
    gap: 2rem;
    flex-direction: column;
  }
}
.search-results-header .results-summary {
  display: flex;
  gap: 4rem;
}
@media (width <= 1439px) {
  .search-results-header .results-summary {
    border-top: 1px solid var(--color-base-grey-300);
    border-bottom: 1px solid var(--color-base-grey-300);
    padding: 1rem 0;
    justify-content: space-between;
  }
}
.search-results-header .results-summary .result-data {
  font-weight: 700;
}
.search-results-header .results-options {
  display: flex;
  gap: 1rem;
  white-space: nowrap;
  flex-wrap: wrap;
}
.search-results-header .results-options button.selected {
  background-color: var(--color-base-grey-200);
}
.search-results-header .results-options button:hover {
  background-color: var(--color-base-grey-200);
}
.search-results-header .results-options :deep(.ds-dropdown) .menu {
  right: 0.25rem;
}
.search-results-header .results-options button.sort-button {
  background-color: var(--color-base-white);
  border: 2px solid var(--color-primary-500);
}
.search-results-header .results-options button.sort-button .icon {
  transform: rotate(90deg);
}
.search-results-header .results-options button.sort-button.is-open .icon {
  transform: rotate(-90deg);
}

.search-quicklinks {
  padding: 2rem 0;
}
.search-quicklinks .h3 {
  font-family: Gotham;
  font-weight: 600;
}
.search-quicklinks ul {
  list-style: none;
  margin-left: 0;
  font-size: 1.25rem;
  line-height: 2.5rem;
}
.search-quicklinks ul li a {
  color: var(--color-base-grey-500);
}

.search-result-nav-buttons {
  display: flex;
  justify-content: space-around;
}
.search-result-nav-buttons button {
  bottom: 0.25rem;
}
.search-result-nav-buttons button:disabled {
  background-color: var(--color-base-white);
}
.search-result-nav-buttons button.selected {
  background-color: var(--color-base-grey-200);
}
</style>
